import { useState,useEffect } from 'react';
import { sendPasswordResetEmail } from "firebase/auth";
import { useAuth} from '@/contexts/AuthContext';
import { auth } from '@/contexts/AuthContext'; 
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Save, Bell, Moon, Sun } from 'lucide-react';
import { ref,get, set } from 'firebase/database';


export default function SettingsPage() {
  const { authToken, userProfile} = useAuth();
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
  return localStorage.getItem("theme") === "dark";
});

  const [notifications, setNotifications] = useState(true);
  const [fullName, setFullName] = useState(userProfile?.full_name || '');
  const [collegeName, setCollegeName] = useState(userProfile?.college_name || '');
  const [district, setDistrict] = useState(userProfile?.district || '');
  const [state, setState] = useState(userProfile?.state || '');

  useEffect(() => {
  if (darkMode) {
    document.documentElement.classList.add("dark");
    localStorage.setItem("theme", "dark");
  } else {
    document.documentElement.classList.remove("dark");
    localStorage.setItem("theme", "light");
  }
}, [darkMode]);
 
  const handleSave = async () => {
    setLoading(true);
    try {
      await updateProfile({
        full_name: fullName,
        college_name: collegeName,
        district: district,
        state: state
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setLoading(false);
  };

  const handleChangePassword = async () => {
  if (!userProfile?.email) {
    toast.error("No email found to send reset link");
    return;
  }

  try {
    await sendPasswordResetEmail(auth, userProfile.email);
    toast.success("Password reset email sent successfully");
  } catch (error) {
    console.error(error);
    toast.error("Failed to send password reset email");
  }
};


  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50" data-testid="settings-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Settings</h1>
          <p className="text-gray-600">Manage your account and preferences</p>
        </div>

        <div className="space-y-6">
          {/* Profile Settings */}
          <Card className="glass border-white/50">
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="fullname">Full Name</Label>
                <Input
                  id="fullname"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  data-testid="settings-name-input"
                />
              </div>
              <div>
                <Label htmlFor="college">College Name or Company</Label>
                <Input
                  id="college"
                  value={collegeName}
                  onChange={(e) => setCollegeName(e.target.value)}
                  data-testid="settings-college-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="district">District</Label>
                  <Input
                    id="district"
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    data-testid="settings-district-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    data-testid="settings-state-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600"
                data-testid="save-settings-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>

          {/* Preferences */}
          

          {/* Account */}
          <Card className="glass border-white/50">
            <CardHeader>
              <CardTitle>Account</CardTitle>
              <CardDescription>Manage your account settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Email: {userProfile?.email}
                </p>
                <Button variant="outline" data-testid="change-password-button" onClick={handleChangePassword}>
                  Change Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}